import { useQuery } from '@tanstack/react-query';

import { unwrap } from '@/api/envelope';
import { readArc } from '@/api/generated';
import { useAuth } from '@/auth/useAuth';
import { useViewingAs } from '@/auth/useViewingAs';
import { arcRoot } from '@/lib/queryRoots';

/*
 * The draft ARC's read, shared by the ARC Builder and the links sub-pages
 * (which show whether each header button is on). One key and one query
 * function, so both readers share one cache entry and its shape cannot
 * diverge.
 */

/**
 * The draft ARC's query key, one per organization.
 *
 * The organization's id is in the key because switching organization does not
 * clear the cache: without it, a Super Admin in two organizations could see
 * the other one's ARC for a moment after switching (the lesson of the top
 * bar's logo, `organizationProfileKey`). Every key starts with `arcRoot`, the
 * prefix another section invalidates (custom-pages P24).
 */
export const arcKey = (organizationId: string | null) =>
  [...arcRoot(organizationId), 'draft'] as const;

/**
 * Which organization's ARC the page is showing: the one staff are viewing,
 * or the signed-in person's own.
 */
export function useArcOrganizationId(): string | null {
  const { user } = useAuth();
  const { viewing } = useViewingAs();
  return viewing ? viewing.id : (user?.organizationId ?? null);
}

/**
 * The draft ARC. The first read of an organization's ARC creates it, with
 * its six standard sections, so there is always one to show.
 */
export function useArcDraft() {
  const organizationId = useArcOrganizationId();
  return useQuery({
    queryKey: arcKey(organizationId),
    queryFn: async () => {
      const reply = await readArc({ throwOnError: true });
      return unwrap(reply.data);
    },
    enabled: organizationId !== null,
  });
}
